/**
 * Documents API Client
 *
 * API client for document upload, management, and retrieval operations.
 */

import { ApiError, extractErrorMessage } from './core'
import { config } from '../../config'
import { logger } from '../../utils/logger'
import { normalizeFileForUpload } from '../fileApi'
import { createServiceFetch } from '../../utils/validateFetchUrl'
import type {
  DocumentUploadResponse,
  DocumentBulkUploadResponse,
  DocumentDetail,
  DocumentListResponse,
  DocumentUpdateRequest,
  ProcessingStatus,
  ChunkListResponse,
  DocumentConfig,
  DocumentConfigUpdate,
  OcrCapabilitiesResponse,
  DocumentSearchRequest,
  DocumentSearchResponse,
  BulkDeleteRequest,
  BulkDeleteResponse,
  BulkReprocessRequest,
  BulkReprocessResponse,
  DocumentStats,
  DocumentFilter,
  DocumentSortConfig,
} from '@/types/documents'

const { api: apiConfig, auth: authConfig } = config
// CRITICAL: Use agentserveUrl from config (defaults to /agentserve), then add /api/v1
// DO NOT use apiConfig.baseUrl (/api) - that routes to TAGP backend, not Agentserve
const AGENTSERVE_API_BASE = `${apiConfig.agentserveUrl}/api/v1`
const documentsFetch = createServiceFetch(AGENTSERVE_API_BASE)

/**
 * Get auth headers for authenticated requests
 */
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}

  let token: string | null = null
  try {
    token = localStorage.getItem(authConfig.tokenKey)
  } catch {
    // Continue without token
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  // Get tenant ID
  let tenantId: string | null = null
  try {
    tenantId = localStorage.getItem('tagp-tenant-id')
  } catch {
    // Continue without tenant
  }

  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId
  }

  return headers
}

/**
 * Make an authenticated request to the Agentserve API.
 * This bypasses core.ts request() which uses the TAGP backend base URL.
 */
async function agentserveRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${AGENTSERVE_API_BASE}${endpoint}`

  const headers: Record<string, string> = {
    ...getAuthHeaders(),
    ...((options.headers as Record<string, string>) || {}),
  }

  // Add Content-Type for JSON requests if not specified and body exists
  if (options.body && typeof options.body === 'string' && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json'
  }

  const response = await documentsFetch(url, {
    ...options,
    headers,
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const message = extractErrorMessage(
      errorData,
      `Request failed with status ${response.status}`
    )
    throw new ApiError(message, response.status, errorData)
  }

  // Handle empty responses
  const text = await response.text()
  if (!text) {
    return undefined as T
  }

  return JSON.parse(text) as T
}

export interface DocumentFileBlob {
  blob: Blob
  filename: string
  contentType: string
  page: number | null
}

function parseContentDispositionFilename(header: string | null): string {
  if (!header) return 'document'
  const utfName = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (utfName?.[1]) {
    try {
      return decodeURIComponent(utfName[1])
    } catch {
      return utfName[1]
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header)
  if (quoted?.[1]) return quoted[1]
  return 'document'
}

async function fetchDocumentBlob(
  agentId: string,
  documentId: string,
  kind: 'file' | 'preview',
  page?: number
): Promise<DocumentFileBlob> {
  const pageQuery = kind === 'preview' && page != null && page >= 1 ? `?page=${page}` : ''
  const url = `${AGENTSERVE_API_BASE}/agents/${agentId}/documents/${documentId}/${kind}${pageQuery}`
  const response = await documentsFetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const message = extractErrorMessage(
      errorData,
      `Request failed with status ${response.status}`
    )
    throw new ApiError(message, response.status, errorData)
  }
  const blob = await response.blob()
  const pageHeader = response.headers.get('X-Citation-Page')
  let parsedPage: number | null = null
  if (pageHeader) {
    const asNumber = Number.parseInt(pageHeader, 10)
    if (Number.isFinite(asNumber) && asNumber >= 1) parsedPage = asNumber
  }
  return {
    blob,
    filename: parseContentDispositionFilename(response.headers.get('Content-Disposition')),
    contentType: response.headers.get('Content-Type') || blob.type || 'application/octet-stream',
    page: parsedPage,
  }
}

/**
 * Download the original stored document (tenant + agent scoped).
 */
export async function downloadDocumentFile(
  agentId: string,
  documentId: string
): Promise<DocumentFileBlob> {
  return fetchDocumentBlob(agentId, documentId, 'file')
}

/**
 * Fetch an inline preview of a stored document, opened to page/slide when given.
 */
export async function previewDocumentFile(
  agentId: string,
  documentId: string,
  page?: number
): Promise<DocumentFileBlob> {
  return fetchDocumentBlob(agentId, documentId, 'preview', page)
}

// ==============================================================================
// Document Upload
// ==============================================================================

/**
 * Upload a single document to an agent's knowledge bank.
 */
export async function uploadDocument(
  agentId: string,
  file: File,
  options?: {
    sessionId?: string
    autoProcess?: boolean
    onProgress?: (progress: number) => void
  }
): Promise<DocumentUploadResponse> {
  const formData = new FormData()
  const normalizedFile = normalizeFileForUpload(file)
  formData.append('file', normalizedFile, normalizedFile.name)

  const params = new URLSearchParams()
  if (options?.sessionId) {
    params.set('session_id', options.sessionId)
  }
  if (options?.autoProcess !== undefined) {
    params.set('auto_process', String(options.autoProcess))
  }

  const querySuffix = params.toString() ? `?${params.toString()}` : ''
  const url = `${AGENTSERVE_API_BASE}/agents/${agentId}/documents/upload${querySuffix}`

  // Use XMLHttpRequest for progress tracking
  if (options?.onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const authHeaders = getAuthHeaders()

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100)
          options.onProgress?.(progress)
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText))
        } else {
          reject(new ApiError(xhr.responseText || 'Upload failed', xhr.status))
        }
      })

      xhr.addEventListener('error', () => {
        reject(new ApiError('Network error during upload', 0))
      })

      xhr.open('POST', url)
      // Set auth headers
      Object.entries(authHeaders).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value)
      })
      xhr.send(formData)
    })
  }

  // Use fetch for non-progress uploads
  const response = await documentsFetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }))
    throw new ApiError(extractErrorMessage(error, 'Upload failed'), response.status, error)
  }

  return response.json()
}

/**
 * Upload multiple documents at once.
 */
export async function uploadDocuments(
  agentId: string,
  files: File[],
  sessionId?: string
): Promise<DocumentBulkUploadResponse> {
  const formData = new FormData()
  files.forEach((file) => {
    const normalizedFile = normalizeFileForUpload(file)
    formData.append('files', normalizedFile, normalizedFile.name)
  })

  const params = new URLSearchParams()
  if (sessionId) {
    params.set('session_id', sessionId)
  }

  const querySuffix = params.toString() ? `?${params.toString()}` : ''
  const url = `${AGENTSERVE_API_BASE}/agents/${agentId}/documents/upload/bulk${querySuffix}`

  const response = await documentsFetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Bulk upload failed' }))
    throw new ApiError(extractErrorMessage(error, 'Bulk upload failed'), response.status, error)
  }

  return response.json()
}

// ==============================================================================
// Document Management
// ==============================================================================

/**
 * List documents for an agent with filtering and pagination.
 */
export async function listDocuments(
  agentId: string,
  options?: {
    filter?: DocumentFilter
    sort?: DocumentSortConfig
    page?: number
    pageSize?: number
  }
): Promise<DocumentListResponse> {
  const params = new URLSearchParams()

  if (options?.filter?.status) {
    params.set('status', options.filter.status)
  }
  if (options?.filter?.search) {
    params.set('search', options.filter.search)
  }
  if (options?.filter?.tags?.length) {
    params.set('tags', options.filter.tags.join(','))
  }
  if (options?.filter?.includeArchived) {
    params.set('include_archived', 'true')
  }
  if (options?.filter?.includeDuplicates) {
    params.set('include_duplicates', 'true')
  }
  if (options?.page) {
    params.set('page', String(options.page))
  }
  if (options?.pageSize) {
    params.set('page_size', String(options.pageSize))
  }
  if (options?.sort?.field) {
    params.set('sort_by', options.sort.field)
  }
  if (options?.sort?.direction) {
    params.set('sort_order', options.sort.direction)
  }

  const querySuffix = params.toString() ? `?${params.toString()}` : ''
  const endpoint = `/agents/${agentId}/documents${querySuffix}`
  return agentserveRequest<DocumentListResponse>(endpoint)
}

/**
 * Get document details by ID.
 */
export async function getDocument(agentId: string, documentId: string): Promise<DocumentDetail> {
  return agentserveRequest<DocumentDetail>(`/agents/${agentId}/documents/${documentId}`)
}

/**
 * Update document metadata.
 */
export async function updateDocument(
  agentId: string,
  documentId: string,
  updates: DocumentUpdateRequest
): Promise<DocumentDetail> {
  return agentserveRequest<DocumentDetail>(`/agents/${agentId}/documents/${documentId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  })
}

/**
 * Delete a document.
 */
export async function deleteDocument(
  agentId: string,
  documentId: string,
  deleteFile = true
): Promise<void> {
  return agentserveRequest<void>(
    `/agents/${agentId}/documents/${documentId}?delete_file=${deleteFile}`,
    {
      method: 'DELETE',
    }
  )
}

/**
 * Reprocess a document.
 */
export async function reprocessDocument(
  agentId: string,
  documentId: string,
  deleteExistingChunks = true
): Promise<ProcessingStatus> {
  return agentserveRequest<ProcessingStatus>(
    `/agents/${agentId}/documents/${documentId}/reprocess?delete_existing_chunks=${deleteExistingChunks}`,
    { method: 'POST' }
  )
}

/**
 * Archive a document (soft delete).
 */
export async function archiveDocument(agentId: string, documentId: string): Promise<void> {
  return agentserveRequest<void>(`/agents/${agentId}/documents/${documentId}/archive`, {
    method: 'POST',
  })
}

// ==============================================================================
// Processing Status
// ==============================================================================

/**
 * Get document processing status.
 */
export async function getDocumentStatus(
  agentId: string,
  documentId: string
): Promise<ProcessingStatus> {
  return agentserveRequest<ProcessingStatus>(`/agents/${agentId}/documents/${documentId}/status`)
}

/**
 * Get document chunks.
 */
export async function getDocumentChunks(
  agentId: string,
  documentId: string,
  page = 1,
  pageSize = 20
): Promise<ChunkListResponse> {
  return agentserveRequest<ChunkListResponse>(
    `/agents/${agentId}/documents/${documentId}/chunks?page=${page}&page_size=${pageSize}`
  )
}

// ==============================================================================
// Document Configuration
// ==============================================================================

/**
 * Get agent's document configuration.
 */
export async function getDocumentConfig(agentId: string): Promise<DocumentConfig> {
  return agentserveRequest<DocumentConfig>(`/agents/${agentId}/documents/config`)
}

/**
 * Update agent's document configuration.
 */
export async function updateDocumentConfig(
  agentId: string,
  updates: DocumentConfigUpdate
): Promise<DocumentConfig> {
  return agentserveRequest<DocumentConfig>(`/agents/${agentId}/documents/config`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  })
}

/**
 * OCR engines available on this Agentserve worker.
 */
export async function getOcrCapabilities(agentId: string): Promise<OcrCapabilitiesResponse> {
  return agentserveRequest<OcrCapabilitiesResponse>(
    `/agents/${agentId}/documents/ocr-capabilities`
  )
}

// ==============================================================================
// Document Search
// ==============================================================================

/**
 * Search documents using vector similarity.
 */
export async function searchDocuments(
  agentId: string,
  requestData: DocumentSearchRequest,
  includeContext = true
): Promise<DocumentSearchResponse> {
  return agentserveRequest<DocumentSearchResponse>(
    `/agents/${agentId}/documents/search?include_context=${includeContext}`,
    {
      method: 'POST',
      body: JSON.stringify(requestData),
    }
  )
}

// ==============================================================================
// Bulk Operations
// ==============================================================================

/**
 * Delete multiple documents.
 */
export async function bulkDeleteDocuments(
  agentId: string,
  requestData: BulkDeleteRequest
): Promise<BulkDeleteResponse> {
  return agentserveRequest<BulkDeleteResponse>(`/agents/${agentId}/documents/bulk-delete`, {
    method: 'POST',
    body: JSON.stringify(requestData),
  })
}

/**
 * Reprocess multiple documents.
 */
export async function bulkReprocessDocuments(
  agentId: string,
  requestData: BulkReprocessRequest
): Promise<BulkReprocessResponse> {
  return agentserveRequest<BulkReprocessResponse>(`/agents/${agentId}/documents/bulk-reprocess`, {
    method: 'POST',
    body: JSON.stringify(requestData),
  })
}

// ==============================================================================
// Statistics
// ==============================================================================

/**
 * Get document statistics for an agent.
 */
export async function getDocumentStats(agentId: string): Promise<DocumentStats> {
  return agentserveRequest<DocumentStats>(`/agents/${agentId}/documents/stats`)
}

// ==============================================================================
// SSE Status Streaming
// ==============================================================================

/**
 * EventSource-like object returned by subscribeToDocumentStatus.
 * Uses fetch instead of native EventSource to support Authorization headers.
 */
interface SSEConnection {
  close: () => void
}

/**
 * Subscribe to document processing status updates via SSE.
 * Uses fetch with Authorization headers instead of native EventSource
 * (which cannot send custom headers).
 *
 * Returns an object with a close() method to abort the connection.
 */
export function subscribeToDocumentStatus(
  agentId: string,
  documentId: string,
  callbacks: {
    onProgress?: (data: { stage: string; progress: number; message?: string }) => void
    onComplete?: (data: { document_id: string; chunks_created: number }) => void
    onError?: (error: string) => void
  }
): SSEConnection {
  const url = `${AGENTSERVE_API_BASE}/agents/${agentId}/documents/${documentId}/status/stream`
  const controller = new AbortController()
  let isClosed = false

  // Map event types to progress values
  const eventProgressMap: Record<string, number> = {
    started: 0,
    extracted: 25,
    chunked: 50,
    embedded: 75,
  }

  // Start the SSE connection with auth headers
  const startStream = async () => {
    try {
      const response = await documentsFetch(url, {
        method: 'GET',
        headers: {
          ...getAuthHeaders(),
          Accept: 'text/event-stream',
        },
        signal: controller.signal,
      })

      if (!response.ok) {
        if (response.status === 401) {
          callbacks.onError?.('Authentication failed - please log in again')
        } else {
          callbacks.onError?.(`HTTP ${response.status}: ${response.statusText}`)
        }
        return
      }

      const reader = response.body?.getReader()
      if (!reader) {
        callbacks.onError?.('No response body')
        return
      }

      const decoder = new TextDecoder()
      let buffer = ''
      let pendingEvent = ''
      let pendingData = ''

      const processEvent = (eventType: string, data: string) => {
        if (isClosed) return

        try {
          const parsed = JSON.parse(data)

          switch (eventType) {
            case 'connected':
              // Initial connection established
              break
            case 'started':
            case 'extracted':
            case 'chunked':
            case 'embedded':
              callbacks.onProgress?.({
                stage: eventType,
                progress: eventProgressMap[eventType] ?? 0,
                message: parsed.message,
              })
              break
            case 'completed':
              callbacks.onComplete?.(parsed)
              isClosed = true
              break
            case 'failed':
              callbacks.onError?.(parsed.error || 'Processing failed')
              isClosed = true
              break
            case 'fallback':
              logger.debug('SSE fallback', undefined, { message: parsed.message })
              callbacks.onError?.('fallback')
              isClosed = true
              break
            case 'timeout':
              callbacks.onError?.('Connection timeout')
              isClosed = true
              break
            case 'heartbeat':
              // Heartbeat - keep connection alive
              break
          }
        } catch (e) {
          logger.error('Failed to parse SSE data', e instanceof Error ? e : undefined, { eventType, data })
        }
      }

      const processLines = (lines: string[]) => {
        for (const line of lines) {
          if (line.startsWith('event:')) {
            pendingEvent = line.slice(6).trim()
          } else if (line.startsWith('data:')) {
            if (pendingData) {
              pendingData += '\n' + line.slice(5)
            } else {
              pendingData = line.slice(5).trim()
            }
          } else if (line === '' && pendingEvent && pendingData) {
            processEvent(pendingEvent, pendingData)
            pendingEvent = ''
            pendingData = ''
          }
        }
      }

      // Read the stream
      while (!isClosed) {
        const { done, value } = await reader.read()

        if (value) {
          buffer += decoder.decode(value, { stream: !done })
          const lines = buffer.split('\n')
          // Keep the last incomplete line in the buffer
          buffer = lines.pop() || ''
          processLines(lines)
        }

        if (done) {
          // Process any remaining buffer content
          if (buffer) {
            processLines([buffer])
          }
          break
        }
      }
    } catch (error) {
      if (isClosed) return // Ignore errors after close

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          // Connection was closed intentionally
          return
        }
        callbacks.onError?.(error.message)
      } else {
        callbacks.onError?.('Connection to status stream failed')
      }
    }
  }

  // Start streaming
  startStream()

  // Return EventSource-like interface
  return {
    close: () => {
      isClosed = true
      controller.abort()
    },
  }
}

// ==============================================================================
// Default Export
// ==============================================================================

export const documentsApi = {
  // Upload
  uploadDocument,
  uploadDocuments,

  // Management
  listDocuments,
  getDocument,
  updateDocument,
  deleteDocument,
  reprocessDocument,
  archiveDocument,

  // Status
  getDocumentStatus,
  getDocumentChunks,
  subscribeToDocumentStatus,

  // Config
  getDocumentConfig,
  updateDocumentConfig,
  getOcrCapabilities,

  // Search
  searchDocuments,

  // Bulk
  bulkDeleteDocuments,
  bulkReprocessDocuments,

  // Stats
  getDocumentStats,

  // Artifact preview
  downloadDocumentFile,
  previewDocumentFile,
}

export default documentsApi
